import React, { useEffect, useMemo, useState } from 'react';

import Popover from 'Views/Components/_HumanWritten/Popover';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import { ColumnConfig, useStaffChartersStore } from './StaffChartersFilterStore';
import { CharterColumnKeys } from './CharterColumnKeys';

interface StaffChartersColumnSelectorProps {
	/** On a customer's page: the customer column is not drawn, so it is not offered either. */
	hideCustomerColumn?: boolean;
}

/**
 * Which columns the charters table draws.
 *
 * Held locally while the popover is open and committed on close, the same way the tables beside it
 * do it: ticking four columns would otherwise re-render the table four times.
 *
 * The charter's name is locked — it is what the row is — so it is shown ticked and cannot be
 * cleared, and is left out of the "x/y" count.
 */
export const StaffChartersColumnSelector = ({ hideCustomerColumn }: StaffChartersColumnSelectorProps) => {
	const { visibleColumns, setVisibleColumns } = useStaffChartersStore();

	const [tempColumns, setTempColumns] = useState<ColumnConfig[]>(visibleColumns);

	useEffect(() => {
		setTempColumns(visibleColumns);
	}, [visibleColumns]);

	// What the popover offers. The store still holds the customer column either way — hiding it here
	// must not clear the reader's own choice for the staff-wide table, which shares this store.
	const displayColumns = useMemo(
		() =>
			hideCustomerColumn
				? tempColumns.filter(col => col.key !== CharterColumnKeys.CUSTOMER)
				: tempColumns,
		[tempColumns, hideCustomerColumn],
	);

	const visibleCount = displayColumns.filter(col => col.visible && !col.locked).length;
	const totalCount = displayColumns.filter(col => !col.locked).length;

	const onAfterClose = () => {
		// Compared by key rather than by index: the store rebuilds its columns from the current
		// definitions on rehydrate, so the two arrays are not guaranteed to line up positionally.
		const hasChanges = tempColumns.some(
			col => col.visible !== visibleColumns.find(x => x.key === col.key)?.visible,
		);

		if (hasChanges) {
			setVisibleColumns(tempColumns);
		}
	};

	const handleToggle = (key: string) => {
		setTempColumns(prev =>
			prev.map(col => (col.key === key && !col.locked ? { ...col, visible: !col.visible } : col)),
		);
	};

	return (
		<Popover
			popoverClassName="fh-popover column-selector-popover"
			align="right"
			onAfterClose={onAfterClose}
			buttonContent={
				<>
					<p className="grey">Columns</p>
					<span>
						{visibleCount}/{totalCount}
					</span>
				</>
			}
			popoverContent={() => (
				<>
					<Checkbox
						className="fh-popover__checkbox fh-popover__list-item"
						model={{ value: visibleCount === totalCount }}
						modelProperty="value"
						label="All"
						onChecked={(_, value) =>
							setTempColumns(prev =>
								prev.map(col =>
									// A column the popover is not showing is left alone by "All".
									col.locked || !displayColumns.some(x => x.key === col.key)
										? col
										: { ...col, visible: value },
								),
							)
						}
					/>
					<div className="column-selector__list">
						{displayColumns.map(column => (
							<Checkbox
								key={column.key}
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: column.visible }}
								modelProperty="value"
								label={column.label}
								isReadOnly={column.locked}
								onChecked={() => handleToggle(column.key)}
							/>
						))}
					</div>
				</>
			)}
		/>
	);
};
